import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";

/** Lib Imports */
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Password } from "primereact/password";
import { InputText } from "primereact/inputtext";
import MolForm, { Field } from "@moltech/molui/molform";
import Toast from "@moltech/molui/toast";


/** Utilities */
import { authentication } from "../../app/slice/authSlice";
import {
  createCookie,
  getCookie,
  isCookieExist,
  removeCookie,
} from "../../utils/cookies/CookieHandler";
import {
  validateEmail,
} from "../../utils/validators/FieldsValidators.js";
import { encryptUrlData } from "../../utils/encryption/CryptoService.js";
import { PreloadDropdownData } from "../../utils/common/PreloadDropdownData"

/** RTK Query */
import { useLoginMutation } from "../../app/services/authApi.js";

/** CSS */
import "./Login.scss";

const initialValue = {
  email: "",
  password: "",
  rememberMe: false,
};

const Login = () => {
  const loginFormRef = useRef();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [formValues, setFormValues] = useState(initialValue);
  const [rememberMe, setRememberMe] = useState(formValues.rememberMe);

  const [
    login,
    { isLoading: isLoginLoading, isSuccess: isLoginSuccess, data: loginData },
  ] = useLoginMutation();

  useEffect(() => {
    if (isCookieExist("rememberMe")) {
      const rememberMeCookie = getCookie("rememberMe");
      const cookieData = {
        email: rememberMeCookie.email,
        password: rememberMeCookie.password,
        rememberMe: true,
      };
      setFormValues({ ...cookieData });
    } else {
      resetForm();
    }
  }, []);

  useEffect(() => {
    if (isLoginSuccess && loginData) {
      if (loginData.isAuthenticated === true) {
        dispatch(authentication(loginData));

        if (!loginData.user?.isResetPasswordRequired) {
          PreloadDropdownData(dispatch);
        }

        if (rememberMe) {
          const formData = loginFormRef.current?.getData();
          const cookieValue = {
            email: formData.email,
            password: formData.password,
          };
          const cookieData = {
            cookieName: "rememberMe",
            cookieValue: cookieValue,
            path: "/",
            expirationTime: 60,
          };
          createCookie(cookieData);
        } else if (isCookieExist("rememberMe")) {
          removeCookie("rememberMe");
        }
        const encryptedEmail = encodeURIComponent(
          encryptUrlData(loginData.user.email)
        );

        if (loginData.user?.isResetPasswordRequired) {
          navigate(`/reset-password/${encryptedEmail}`);
        } else {
          navigate("/");
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoginSuccess, loginData]);

  const resetForm = () => {
    setFormValues(initialValue);
  };

  const handleFormSubmit = () => {
    const formData = loginFormRef.current?.getData();
    if (formData) {
      const formReq = {
        email: formData.email,
        password: formData.password,
      };
      setRememberMe(formData.rememberMe);
      login(formReq).then((res) => {
        if (res.error) {
          const status = res.error.status;
          const message = res.error.data?.message;
          if (status === 401) {
            Toast.error(message);
          }
        }
      });
    }
  };

  const handleSaveClick = () => {
    if (loginFormRef.current) {
      loginFormRef.current.submit();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveClick();
    }
  };

  return (
    <div className="login-page-wrapper">
      <div className="login-page-card">
        <div className="login-form">
          <div className="login-card">
            <img className="big-logo" src="/images/logo.png" alt="logo" />
            <h2>Welcome to MoltechR</h2>
            <p className="sub-text">Log in to your account to continue.</p>
            <div className="saporate-line"></div>

            <MolForm
              ref={loginFormRef}
              onSubmit={handleFormSubmit}
              initialValues={formValues}
            >
              <div className="form-field">
                <Field
                  component={InputText}
                  name="email"
                  label="Email Address"
                  placeholder="Email Address"
                  validation={validateEmail}
                  onKeyDown={handleKeyDown}
                />
              </div>
              <div className="form-field">
                <Field
                  component={Password}
                  feedback={false}
                  toggleMask
                  name="password"
                  label="Password"
                  placeholder="Password"
                  validation="required"
                  onKeyDown={handleKeyDown}
                // validation={validatePassword}
                />
              </div>
              <div className="d-flex justify-content-between">
                <div className="login-checkbox">
                  <Field
                    className="checkbox-field"
                    component={Checkbox}
                    name="rememberMe"
                    label="Remember Me"
                  />
                </div>
                <div className="forgot-pass-sec mt-1" >
                  <Link to={"/forgot-password"} tabIndex={-1}>Forgot Password?</Link>
                </div>
              </div>
            </MolForm>
            <Button
              label="Login"
              loading={isLoginLoading}
              className="btn primary-btn login-btn"
              onClick={handleSaveClick}
            />
            <div className="bottom-text">
              <span>
                Streamline Your Hiring Process with MoltechR. Manage job
                applications, organize interview workflows, and track
                candidate progress all in one centralized platform.
              </span>
            </div>
          </div>
        </div>
        <div className="login-image">
          <img
            src="./images/login-bg.png"
            alt="HRMS"
          />
        </div>
      </div>
    </div>
  );
};

export default Login;
